/**
 * React Query hooks for remote sessions.
 * Distinct from in-person session pages. Source: seances-spec.md §30.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as sessionsRemoteService from '@/services/remote/sessions.service';
import type { QueryParams, SessionLiveKitPresenceResponse } from '@/types/remote/session.types';
import type {
  Session,
  SessionWithParticipants,
  CreateSessionDto,
  UpdateSessionDto,
} from '@/types/inperson/session.types';

/** React Query keys for remote sessions (`/sessions`). */
export const sessionKeys = {
  all: ['sessions-remote'] as const,
  lists: () => [...sessionKeys.all, 'list'] as const,
  list: (params?: QueryParams) => [...sessionKeys.lists(), { params }] as const,
  details: () => [...sessionKeys.all, 'detail'] as const,
  detail: (id: string) => [...sessionKeys.details(), id] as const,
  livekit: (sessionId: string) => [...sessionKeys.all, 'livekit', sessionId] as const,
  livekitPresence: (sessionId: string) => [...sessionKeys.livekit(sessionId), 'presence'] as const,
};

/** `GET /sessions/:id` */
export const useSession = (
  id: string | null | undefined,
): UseQueryResult<SessionWithParticipants> => {
  return useQuery({
    queryKey: sessionKeys.detail(id!),
    queryFn: () => sessionsRemoteService.getSession(id!),
    enabled: !!id,
  });
};

/** `POST /sessions` — invalidates session lists. */
export const useCreateSession = (): UseMutationResult<
  Session | Session[],
  Error,
  CreateSessionDto
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sessionsRemoteService.createSession,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
};

/** `PATCH /sessions/:id` — invalidates list + detail. */
export const useUpdateSession = (): UseMutationResult<
  Session,
  Error,
  { id: string; dto: UpdateSessionDto }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }) => sessionsRemoteService.updateSession(id, dto),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(variables.id) });
    },
  });
};

/** `GET /sessions/:id/livekit/presence` — polls room presence. */
export const useSessionLiveKitPresence = (
  sessionId: string | null | undefined,
  options?: { refetchInterval?: number | false },
): UseQueryResult<SessionLiveKitPresenceResponse> => {
  return useQuery({
    queryKey: sessionKeys.livekitPresence(sessionId!),
    queryFn: () => sessionsRemoteService.getSessionLiveKitPresence(sessionId!),
    enabled: !!sessionId,
    refetchInterval: options?.refetchInterval ?? 5000,
  });
};
